import React, { useEffect, useState } from 'react'
import {
  collection, query, where, orderBy, getDocs,
  addDoc, updateDoc, deleteDoc, doc, getDoc,
  Timestamp, serverTimestamp,
} from 'firebase/firestore'
import { db } from '../utils/firebase'
import { useAuth } from '../contexts/AuthContext'
import { useAppSettings } from '../hooks/useAppSettings'
import { formatDate as fmtDate, formatTime as fmtTime } from '../utils/time'
import { Calendar, MapPin, Clock, Plus, Edit, Trash2, List, ChevronLeft, ChevronRight } from 'lucide-react'
import PaywallModal from '../components/PaywallModal'
import { EventModal } from './AdminPage'
import './EventsPage.css'

/**
 * Resident-facing Events page. Anyone in the neighborhood can view
 * scheduled events; admins / board members / super admins get an
 * inline "+" button that opens the same EventModal used by the
 * Admin → Manage Events surface, so they don't need to hop over to
 * the admin panel just to schedule a block party.
 *
 * Data model: `events/{id}` = { neighborhoodId, title, description,
 *   startAt, endAt, location, createdBy, createdAt, updatedAt }
 */
function EventsPage() {
  const { userProfile, isSubscriptionActive } = useAuth()
  const canManage = !!(userProfile?.isAdmin || userProfile?.isBoardMember || userProfile?.isSuperAdmin)

  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [neighborhoodName, setNeighborhoodName] = useState('')

  // Modal state — mirrors the admin section so the shared EventModal
  // slots in cleanly.
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [startAt, setStartAt] = useState('')
  const [endAt, setEndAt] = useState('')
  const [location, setLocation] = useState('')
  const [saving, setSaving] = useState(false)

  // Calendar / list toggle. Calendar is the default — most users are
  // scanning "what's coming up this month" rather than reading a list.
  const [viewMode, setViewMode] = useState('calendar')
  // Month being viewed in calendar mode (as a Date on the 1st of the month).
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })

  const neighborhoodId = userProfile?.neighborhoodId

  const loadEvents = async () => {
    if (!neighborhoodId) { setLoading(false); return }
    try {
      setLoading(true)
      const snap = await getDocs(query(
        collection(db, 'events'),
        where('neighborhoodId', '==', neighborhoodId),
        orderBy('startAt', 'asc')
      ))
      setEvents(snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        startAt: d.data().startAt?.toDate?.() || null,
        endAt: d.data().endAt?.toDate?.() || null,
      })))
      // Fetch the human-readable neighborhood name for the subtitle,
      // matching the pattern used by DiscussionsPage. Falls back to
      // the id (and finally "your neighborhood") if the lookup fails.
      try {
        const nDoc = await getDoc(doc(db, 'neighborhoods', neighborhoodId))
        setNeighborhoodName(nDoc.exists() ? (nDoc.data().name || '') : '')
      } catch {
        setNeighborhoodName('')
      }
    } catch (e) {
      console.error('Error loading events:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadEvents() /* eslint-disable-next-line */ }, [neighborhoodId])

  const openCreate = (prefillDate = null) => {
    setEditing(null)
    setTitle('')
    setDescription('')
    setEndAt('')
    setLocation('')
    // If a date was clicked in the calendar, pre-fill the start with
    // that date at a sensible default time (noon local). Users can
    // still edit before saving.
    if (prefillDate) {
      const pad = (n) => String(n).padStart(2, '0')
      const d = new Date(prefillDate)
      d.setHours(12, 0, 0, 0)
      setStartAt(
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
      )
    } else {
      setStartAt('')
    }
    setShowModal(true)
  }

  const openEdit = (event) => {
    setEditing(event)
    const toLocalInput = (d) => {
      if (!d) return ''
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
    setTitle(event.title || '')
    setDescription(event.description || '')
    setStartAt(toLocalInput(event.startAt))
    setEndAt(toLocalInput(event.endAt))
    setLocation(event.location || '')
    setShowModal(true)
  }

  const saveEvent = async () => {
    if (!neighborhoodId) return
    if (!title.trim() || !startAt) return
    if (endAt) {
      const startDate = new Date(startAt)
      const endDate = new Date(endAt)
      if (!isNaN(endDate.getTime()) && endDate.getTime() <= startDate.getTime()) {
        alert('End date/time must be after the start date/time.')
        return
      }
    }
    setSaving(true)
    try {
      const payload = {
        neighborhoodId,
        title: title.trim(),
        description: description.trim(),
        location: location.trim(),
        startAt: Timestamp.fromDate(new Date(startAt)),
        endAt: endAt ? Timestamp.fromDate(new Date(endAt)) : null,
        updatedAt: serverTimestamp(),
      }
      if (editing) {
        await updateDoc(doc(db, 'events', editing.id), payload)
      } else {
        await addDoc(collection(db, 'events'), {
          ...payload,
          createdBy: userProfile?.email || userProfile?.uid || null,
          createdAt: serverTimestamp(),
        })
      }
      setShowModal(false)
      setEditing(null)
      loadEvents()
    } catch (e) {
      console.error('Error saving event:', e)
    } finally {
      setSaving(false)
    }
  }

  const removeEvent = async (event) => {
    if (!window.confirm(`Delete "${event.title || 'this event'}"? This cannot be undone.`)) return
    try {
      await deleteDoc(doc(db, 'events', event.id))
      loadEvents()
    } catch (e) {
      console.error('Error deleting event:', e)
    }
  }

  const now = new Date()
  const upcoming = events.filter(e => !e.startAt || e.startAt >= now)
  const past = events.filter(e => e.startAt && e.startAt < now).reverse()
  const locationLabel = neighborhoodName || 'your neighborhood'

  return (
    <div className="events-page">
      {!isSubscriptionActive() && <PaywallModal isAdmin={canManage} />}

      <header className="events-header">
        <div className="events-header-text">
          <h1>Events</h1>
          <p className="muted">
            {loading
              ? 'Loading…'
              : `${upcoming.length} upcoming ${upcoming.length === 1 ? 'event' : 'events'} in ${locationLabel}`}
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            className="btn-primary events-new-btn"
            onClick={openCreate}
            aria-label="New event"
            title="New event"
          >
            <Plus size={22} strokeWidth={2.5} />
          </button>
        )}
      </header>

      {/* View toggle: Calendar (default) vs List. */}
      <div className="events-view-toggle segmented" data-walk="events-view-toggle">
        <button
          type="button"
          className={`segmented-item${viewMode === 'calendar' ? ' active' : ''}`}
          onClick={() => setViewMode('calendar')}
        >
          <Calendar size={16} style={{ marginRight: 6 }} /> Calendar
        </button>
        <button
          type="button"
          className={`segmented-item${viewMode === 'list' ? ' active' : ''}`}
          onClick={() => setViewMode('list')}
        >
          <List size={16} style={{ marginRight: 6 }} /> List
        </button>
      </div>

      {loading ? (
        <div className="card events-empty"><p className="muted">Loading events…</p></div>
      ) : viewMode === 'calendar' ? (
        <CalendarView
          month={calendarMonth}
          setMonth={setCalendarMonth}
          events={events}
          canManage={canManage}
          onEdit={openEdit}
          onCreateAtDate={openCreate}
        />
      ) : (
        <>
          <EventsSection
            title="Upcoming"
            events={upcoming}
            emptyMessage={canManage ? 'No upcoming events scheduled. Click + to add one.' : 'No upcoming events scheduled.'}
            canManage={canManage}
            onEdit={openEdit}
            onDelete={removeEvent}
          />
          {past.length > 0 && (
            <EventsSection
              title="Past"
              events={past}
              canManage={canManage}
              onEdit={openEdit}
              onDelete={removeEvent}
              muted
            />
          )}
        </>
      )}

      {showModal && (
        <EventModal
          editing={editing}
          title={title} setTitle={setTitle}
          description={description} setDescription={setDescription}
          startAt={startAt} setStartAt={setStartAt}
          endAt={endAt} setEndAt={setEndAt}
          location={location} setLocation={setLocation}
          saving={saving}
          onSave={saveEvent}
          onClose={() => { setShowModal(false); setEditing(null) }}
        />
      )}
    </div>
  )
}

function EventsSection({ title, events, emptyMessage, canManage, onEdit, onDelete, muted = false }) {
  return (
    <section className={`events-section${muted ? ' past' : ''}`}>
      <h2 className="events-section-title">{title}</h2>
      {events.length === 0 ? (
        <div className="card events-empty"><p className="muted">{emptyMessage}</p></div>
      ) : (
        <div className="events-list">
          {events.map(e => (
            <EventCard
              key={e.id}
              event={e}
              canManage={canManage}
              onEdit={() => onEdit(e)}
              onDelete={() => onDelete(e)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function EventCard({ event, canManage, onEdit, onDelete }) {
  const { settings } = useAppSettings()
  const start = event.startAt
  const end = event.endAt
  const dayLabel = start
    ? fmtDate(start, settings, { weekday: 'short', month: 'short', day: 'numeric', year: undefined })
    : 'Date TBD'
  const timeLabel = start
    ? end
      ? `${fmtTime(start, settings)} – ${fmtTime(end, settings)}`
      : fmtTime(start, settings)
    : ''

  return (
    <div className="card event-card">
      <div className="event-date">
        <div className="event-date-month">
          {start ? fmtDate(start, settings, { month: 'short', day: undefined, year: undefined }).toUpperCase() : '—'}
        </div>
        <div className="event-date-day">{start ? start.getDate() : '?'}</div>
      </div>
      <div className="event-body">
        <h3 className="event-title">{event.title || 'Untitled event'}</h3>
        <div className="event-meta">
          <span className="event-meta-item"><Calendar size={14} />{dayLabel}</span>
          {timeLabel && <span className="event-meta-item"><Clock size={14} />{timeLabel}</span>}
          {event.location && <span className="event-meta-item"><MapPin size={14} />{event.location}</span>}
        </div>
        {event.description && <p className="event-description">{event.description}</p>}
      </div>
      {canManage && (
        <div className="event-actions">
          <button
            type="button"
            className="announcement-action-btn"
            onClick={onEdit}
            title="Edit event"
          >
            <Edit size={16} />
          </button>
          <button
            type="button"
            className="announcement-action-btn danger"
            onClick={onDelete}
            title="Delete event"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}
    </div>
  )
}

// Simple month-grid calendar view. Renders full weeks (Sun–Sat)
// including leading/trailing blanks so a month always fills a
// rectangular grid. Events show as compact pills in their day cell;
// clicking one opens the editor for admins, no-ops for residents.
function CalendarView({ month, setMonth, events, canManage, onEdit, onCreateAtDate }) {
  const { settings } = useAppSettings()
  const today = new Date()
  const year = month.getFullYear()
  const monthIdx = month.getMonth()
  const monthLabel = fmtDate(month, settings, { month: 'long', year: 'numeric', day: undefined })

  // Compute the 6-week grid starting on the Sunday on/before the 1st.
  const firstOfMonth = new Date(year, monthIdx, 1)
  const gridStart = new Date(firstOfMonth)
  gridStart.setDate(firstOfMonth.getDate() - firstOfMonth.getDay())

  const cells = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart)
    d.setDate(gridStart.getDate() + i)
    cells.push(d)
  }

  // Group events by yyyy-mm-dd for quick lookup.
  const eventsByDay = new Map()
  events.forEach(ev => {
    if (!ev.startAt) return
    const key = `${ev.startAt.getFullYear()}-${ev.startAt.getMonth()}-${ev.startAt.getDate()}`
    if (!eventsByDay.has(key)) eventsByDay.set(key, [])
    eventsByDay.get(key).push(ev)
  })

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

  const prevMonth = () => setMonth(new Date(year, monthIdx - 1, 1))
  const nextMonth = () => setMonth(new Date(year, monthIdx + 1, 1))
  const goToday = () => setMonth(new Date(today.getFullYear(), today.getMonth(), 1))

  return (
    <div className="card calendar-view">
      <div className="calendar-header">
        <button type="button" className="btn-ghost small" onClick={prevMonth} aria-label="Previous month">
          <ChevronLeft size={18} />
        </button>
        <div className="calendar-month-label">{monthLabel}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn-ghost small" onClick={goToday}>Today</button>
          <button type="button" className="btn-ghost small" onClick={nextMonth} aria-label="Next month">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="calendar-weekdays">
        {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(d => (
          <div key={d} className="calendar-weekday">{d}</div>
        ))}
      </div>

      <div className="calendar-grid">
        {cells.map((d, i) => {
          const isCurrentMonth = d.getMonth() === monthIdx
          const isToday = isSameDay(d, today)
          const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
          const dayEvents = eventsByDay.get(key) || []
          // Cells for today or a future date are clickable *for admins*
          // to open the New Event modal pre-filled with that date.
          // Past cells stay static.
          const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate())
          const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
          const isPast = dOnly < todayOnly
          const clickable = canManage && !isPast && onCreateAtDate
          const handleCellClick = clickable
            ? (e) => {
                // Ignore bubbled clicks from the event pills so we
                // don't open both an editor and a new-event modal.
                if (e.target.closest('.calendar-event-pill')) return
                onCreateAtDate(d)
              }
            : undefined
          return (
            <div
              key={i}
              className={`calendar-cell${isCurrentMonth ? '' : ' other-month'}${isToday ? ' today' : ''}${clickable ? ' clickable' : ''}`}
              onClick={handleCellClick}
              role={clickable ? 'button' : undefined}
              title={clickable ? 'Click to schedule an event on this day' : undefined}
            >
              <div className="calendar-cell-day">
                <span className={isToday ? 'calendar-day-num today' : 'calendar-day-num'}>
                  {d.getDate()}
                </span>
              </div>
              <div className="calendar-cell-events">
                {dayEvents.slice(0, 3).map(ev => (
                  <button
                    key={ev.id}
                    type="button"
                    className="calendar-event-pill"
                    onClick={() => canManage && onEdit(ev)}
                    title={ev.title}
                    style={{ cursor: canManage ? 'pointer' : 'default' }}
                  >
                    <span className="calendar-event-title">{ev.title || 'Untitled'}</span>
                    {ev.startAt && (
                      <span className="calendar-event-time">
                        {fmtTime(ev.startAt, settings)}
                      </span>
                    )}
                  </button>
                ))}
                {dayEvents.length > 3 && (
                  <span className="calendar-event-more muted">+{dayEvents.length - 3} more</span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default EventsPage
